'use client';
import { useEffect } from "react";
import gsap from "gsap";

const useAnimatedText = (deps = []) => {
	useEffect(() => {
		let textWrap = [];
		let observer;
		let timeoutId;
		
		// Delay to ensure DOM is ready
		const initTimeout = setTimeout(() => {
			textWrap = document.querySelectorAll(".anim-text");

			textWrap.forEach((item) => {
				// Skip if already processed
				if (item.querySelector('.anim-char')) {
					return;
				}
				
				const html = item.innerHTML;
				item.textContent = "";

				const parser = new DOMParser();
				const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");

				const wrapCharTwice = (char) => {
					if (char === " ") {
						const space = document.createElement("span");
						space.classList.add("anim-char");
						space.innerHTML = "&nbsp;";
						space.style.display = "inline-block";
						space.style.width = "0.2em";
						return space;
					}

					const outer = document.createElement("span");
					outer.classList.add("anim-char");
					const inner = document.createElement("span");
					inner.classList.add("anim-char");
					inner.textContent = char;
					outer.appendChild(inner);
					return outer;
				};

				const processNode = (node, parent) => {
					if (node.nodeType === Node.TEXT_NODE) {
						node.textContent.split("").forEach((char) => {
							parent.appendChild(wrapCharTwice(char));
						});
					} else if (node.nodeType === Node.ELEMENT_NODE) {
						const tagName = node.tagName.toLowerCase();
						const elementClone = document.createElement(tagName);
						[...node.attributes].forEach(attr =>
							elementClone.setAttribute(attr.name, attr.value)
						);
						node.childNodes.forEach(child => processNode(child, elementClone));
						parent.appendChild(elementClone);
					}
				};

				[...doc.body.firstChild.childNodes].forEach((node) =>
					processNode(node, item)
				);
			});
		}, 100);

		const animateText = (item, isActive) => {
			const chars = item.querySelectorAll(".anim-char");
			gsap.killTweensOf(chars);

			if (isActive) {
				gsap.to(chars, {
					color: "#fff",
					duration: 0.3,
					stagger: 0.01,
				});
			} else {
				gsap.set(chars, {
					color: "rgba(255, 255, 255, 0.15)",
				});
			}
		};

		observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					const item = entry.target;
					const isActive = item.closest(".swiper-slide")?.classList.contains("swiper-slide-active");

					if (entry.isIntersecting && isActive) {
						animateText(item, true);
					} else if (!entry.isIntersecting) {
						animateText(item, false);
					}
				});
			},
			{ threshold: 0.5 }
		);

		textWrap.forEach((item) => observer.observe(item));

		timeoutId = setTimeout(() => {
			textWrap.forEach((item) => {
				const rect = item.getBoundingClientRect();
				const inView = rect.top < window.innerHeight && rect.bottom >= 0;
				const isActive = item.closest(".swiper-slide")?.classList.contains("swiper-slide-active");

				if (inView && isActive) {
					animateText(item, true);
				}
			});
		}, 100);

		return () => {
			// Clear init timeout
			clearTimeout(initTimeout);
			
			// Clear timeout
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			
			// Disconnect observer
			if (observer) {
				observer.disconnect();
			}
			
			// Kill any remaining animations
			const textWrap = document.querySelectorAll(".anim-text");
			textWrap.forEach((item) => {
				const chars = item.querySelectorAll(".anim-char");
				gsap.killTweensOf(chars);
			});
		};
	}, deps);
};

export default useAnimatedText;
